import React, { useEffect, useRef, useState } from "react";
import PlaidModal, {
  ModalApi as PlaidModalApi,
} from "components/shared/PlaidModal";
import {
  getPaveStatus,
  usePostBankAccountsUnlinkMutation,
} from "store/api/apiSlice";
import { EligibilityCriteria } from "utils/api/cardApplication";
import { ApiUserProfile } from "utils/api/user";
import PendingBankDataMessage from "../PendingBankDataMessage";
import ActionablePositiveEarningsContent from "./ActionablePositiveEarningsContent";
import UnactionableNonNeoBankContent from "./UnactionableNonNeoBankContent";

const getContentComponent = (actionableCriteria: EligibilityCriteria[]) => {
  // If the user links a bank with no positive earnings, their direct deposit may be going elsewhere.
  // In this case, it doesn't matter if it's also a neobank.
  if (actionableCriteria.includes(EligibilityCriteria.positiveEarnings)) {
    return ActionablePositiveEarningsContent;
  }

  return UnactionableNonNeoBankContent;
};

interface Props {
  actionableCriteria: EligibilityCriteria[];
  setIsPolling: (isPolling: boolean) => void;
  userProfile: ApiUserProfile;
}

const BankRelinking: React.FC<Props> = ({
  setIsPolling,
  actionableCriteria,
  userProfile,
}) => {
  const modalRef = useRef<PlaidModalApi>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isPollingPaveStatus, setIsPollingPaveStatus] = useState(true);
  const [postBankAccountsUnlink] = usePostBankAccountsUnlinkMutation();
  const { data: paveStatus } = getPaveStatus({
    pollingInterval: isPollingPaveStatus ? 2000 : 0,
  });

  const openModal = async () => {
    setIsLoading(true);
    await postBankAccountsUnlink();

    if (modalRef && modalRef.current) {
      modalRef.current.open();
      setIsLoading(false);
      setIsPolling(false);
    }
  };

  const onSubmitPlaid = () => {
    setIsLoading(true);
  };

  const onSuccessPlaid = () => {
    setIsPolling(true);
    setIsPollingPaveStatus(true);
  };

  useEffect(() => {
    if (paveStatus?.ready) {
      setIsPollingPaveStatus(false);
    }
  }, [paveStatus]);

  if (!paveStatus?.ready) return <PendingBankDataMessage />;

  const Content = getContentComponent(actionableCriteria);

  return (
    <>
      <Content
        openModal={openModal}
        isLoading={isLoading}
        userProfile={userProfile}
      />

      <PlaidModal
        ref={modalRef}
        onSubmit={onSubmitPlaid}
        onSuccess={onSuccessPlaid}
      />
    </>
  );
};

export default BankRelinking;
